import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@/shared/icons';
import { createPortal } from 'react-dom';
import { Tooltip } from '@/shared/components/ui/Tooltip';

/**
 * Interface para opção do multi-select
 */
export interface MultiSelectOption {
  value: string;
  label: string;
}

/**
 * Props do MultiSelect
 */
export interface MultiSelectProps {
  /** Lista de opções disponíveis */
  options: MultiSelectOption[];
  /** Valores selecionados */
  selectedValues: string[];
  /** Callback quando seleção muda */
  onChange: (values: string[]) => void;
  /** Placeholder do botão */
  placeholder?: string;
  /** Label quando há itens selecionados */
  label?: string;
  /** Estado de loading */
  loading?: boolean;
  /** Classe CSS customizada */
  className?: string;
  'data-testid'?: string;
}

/**
 * Componente de seleção múltipla genérico
 *
 * Permite selecionar múltiplos valores de uma lista através de um dropdown.
 *
 * @example
 * ```tsx
 * <MultiSelect
 *   options={[
 *     { value: 'low', label: 'Baixa' },
 *     { value: 'medium', label: 'Média' },
 *     { value: 'high', label: 'Alta' },
 *   ]}
 *   selectedValues={selectedPriorities}
 *   onChange={setSelectedPriorities}
 *   placeholder="Selecione prioridades"
 *   label="Prioridade"
 * />
 * ```
 */
export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Selecione',
  label: _label,
  loading = false,
  className = '',
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  const handleToggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleRemoveOption = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== value));
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-full h-[38px] rounded-[9px] animate-pulse"
          style={{ backgroundColor: 'var(--theme-border)' }}
        />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Button com valores selecionados */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 min-h-[38px] px-3 py-1.5 rounded-[9px] border text-sm transition-colors"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          borderColor: 'var(--theme-input-border)',
          color: 'var(--theme-input-text)',
        }}
        data-testid={dataTestId}
      >
        <div className="flex items-center gap-2 flex-1 overflow-hidden">
          {selectedOptions.length === 0 ? (
            <span style={{ color: 'var(--theme-muted-text)' }}>{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {selectedOptions.slice(0, 2).map((option) => (
                <span
                  key={option.value}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
                  style={{
                    backgroundColor: 'var(--theme-button-ghost-hover)',
                    color: 'var(--theme-input-text)',
                  }}
                >
                  {option.label}
                  <button
                    type="button"
                    onClick={(e) => handleRemoveOption(option.value, e)}
                    className="rounded-full p-0.5"
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedOptions.length > 2 && (
                <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                  +{selectedOptions.length - 2}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {selectedOptions.length > 0 && (
            <Tooltip content="Limpar seleção">
              <button
                type="button"
                onClick={handleClearAll}
                className="p-1 rounded-full"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                data-testid="multiselect-clear"
              >
                <Icon name="x" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              </button>
            </Tooltip>
          )}
          <Icon
            name="chevron-down"
            className="w-4 h-4"
            style={{ color: 'var(--theme-muted-text)' }}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && createPortal(
        <>
          {/* Backdrop — z acima do NodeConfigPanel/modais (z-201) para o dropdown
              (portaled ao body) não ficar atrás do painel de config do node. */}
          <div
            className="fixed inset-0 z-[300]"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown content */}
          <div
            className="fixed w-72 rounded-md shadow-lg z-[301] max-h-80 flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
            }}
          >
            {/* Search */}
            <div className="p-3 border-b" style={{ borderColor: '#e5e7eb' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="w-full px-3 py-1.5 text-sm rounded border"
                style={{
                  backgroundColor: '#f9fafb',
                  borderColor: '#d1d5db',
                  color: '#1e293b'
                }}
                autoFocus
              />
            </div>

            {/* Options list */}
            <div className="overflow-y-auto flex-1 p-2">
              {filteredOptions.length === 0 ? (
                <div className="text-center py-4 text-sm" style={{ color: '#9ca3af' }}>
                  Nenhuma opção encontrada
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleToggleOption(option.value)}
                        // Derivado do testid do próprio campo — dá um alvo estável
                        // por opção (o dropdown é portaled ao body, então buscar
                        // por texto pega qualquer lista aberta na página).
                        data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded text-sm transition-colors text-left"
                        style={{
                          backgroundColor: isSelected ? '#f3f4f6' : 'transparent',
                          color: '#1e293b'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <span className="flex-1">{option.label}</span>
                        {isSelected && (
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: '#8b5cf6' }}
                          >
                            <Icon name="check" className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer com contador */}
            {selectedOptions.length > 0 && (
              <div
                className="px-3 py-2 border-t text-xs flex items-center justify-between"
                style={{
                  borderColor: '#e5e7eb',
                  color: '#64748b'
                }}
              >
                <span>
                  {selectedOptions.length} item{selectedOptions.length !== 1 ? 's' : ''} selecionado{selectedOptions.length !== 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                  className="text-xs hover:underline"
                  style={{ color: '#8b5cf6' }}
                >
                  Limpar
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
